<template>
  <div class="note-editor rich-text-editor" :class="{'note-editor--focus': focused}">
    <div class="note-editor__toolbar" role="toolbar">
      <template v-for="(group, groupIndex) in toolbar">
        <span v-if="groupIndex" :key="'separator-' + groupIndex" class="rich-text-editor__separator"></span>
        <span v-for="button in group"
              :key="button.command"
              class="note-editor__button"
              :class="{'note-editor__button--on': active[button.command]}"
              role="button"
              :aria-pressed="active[button.command] ? 'true' : 'false'"
              :aria-label="$t(button.label)"
              v-b-tooltip.hover
              :title="$t(button.label)"
              :data-qid="'rich-text-' + button.command"
              @mousedown.prevent="run(button.command)"
        >
          <font-awesome-icon v-if="button.icon" :icon="button.icon"/>
          <span v-else class="rich-text-editor__text-icon">{{ button.text }}</span>
        </span>
      </template>
    </div>
    <div ref="input"
         class="note-editor__input rich-text"
         :style="{minHeight: (rows * 1.5) + 'em'}"
         contenteditable="true"
         role="textbox"
         aria-multiline="true"
         :aria-label="placeholder"
         :data-placeholder="placeholder"
         :data-qid="qid"
         @input="onInput"
         @keydown="onKeydown"
         @paste="onPaste"
         @focus="onFocus"
         @blur="focused = false"
    ></div>
  </div>
</template>

<script>
import { richTextToHtml, richTextFromElement, richTextFromHtml, rankHeadings, listKind, NESTED_LIST_KIND, MAX_LIST_DEPTH } from './richText';

// Text field for the management statement and system description (US 9394): bold, italic, underline,
// Header 2 and Header 3, and bulleted, numbered and lettered lists up to three levels deep.
// Task text fields use it without headers, and compact (see richText.js).
// v-model holds the stored format described in richText.js.
export default {
  name: 'RichTextEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    rows: {
      type: Number,
      default: 5
    },
    qid: {
      type: String,
      default: null
    },
    headings: {
      type: Boolean,
      default: true
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      buttons: [
        [
          { command: 'bold', icon: 'bold', label: 'tasks.formatting.bold' },
          { command: 'italic', icon: 'italic', label: 'tasks.formatting.italic' },
          { command: 'underline', icon: 'underline', label: 'tasks.formatting.underline' }
        ],
        [
          { command: 'h2', icon: 'h2', label: 'tasks.formatting.heading_2', heading: true },
          { command: 'h3', icon: 'h3', label: 'tasks.formatting.heading_3', heading: true }
        ],
        [
          { command: 'bullet', icon: 'list-ul', label: 'tasks.formatting.bullet_list' },
          { command: 'number', icon: 'list-ol', label: 'tasks.formatting.numbered_list' },
          { command: 'letter', text: 'a.', label: 'tasks.formatting.letter_list' },
          { command: 'outdent', icon: 'outdent', label: 'tasks.formatting.outdent' },
          { command: 'indent', icon: 'indent', label: 'tasks.formatting.indent' }
        ]
      ],
      active: { bold: false, italic: false, underline: false, h2: false, h3: false, bullet: false, number: false, letter: false, outdent: false, indent: false },
      focused: false,
      emitted: null
    };
  },
  computed: {
    toolbar() {
      return this.headings ? this.buttons : this.buttons.filter((group) => !group[0].heading);
    },
    options() {
      return { headings: this.headings ? undefined : false, compact: this.compact };
    }
  },
  watch: {
    value(value) {
      // Only redraw for changes from outside (discard, an imported document), otherwise the caret would
      // jump to the start while typing
      if (value !== this.emitted) {
        this.render();
      }
    }
  },
  mounted() {
    this.render();
    document.addEventListener('selectionchange', this.updateActive);
    this.$nextTick(() => this.focusEnd());
  },
  beforeDestroy() {
    document.removeEventListener('selectionchange', this.updateActive);
  },
  methods: {
    render() {
      this.$refs.input.innerHTML = richTextToHtml(this.value, { headings: this.options.headings });
      this.emitted = this.value;
    },
    focusEnd() {
      const input = this.$refs.input;
      input.focus();
      const range = document.createRange();
      range.selectNodeContents(input);
      range.collapse(false);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    },
    onFocus() {
      this.focused = true;
      // New lines are paragraphs, and bold is <b> instead of <span style="...">
      document.execCommand('defaultParagraphSeparator', false, 'p');
      document.execCommand('styleWithCSS', false, false);
    },
    run(command) {
      if (document.activeElement !== this.$refs.input) {
        this.focusEnd();
      }
      if ((command === 'h2' || command === 'h3') && this.headings) {
        // The paragraph with the caret becomes a header, or a normal paragraph again
        document.execCommand('formatBlock', false, this.currentBlock() === command ? 'p' : command);
      } else if (command === 'bullet' || command === 'number' || command === 'letter') {
        this.toggleList(command);
      } else if (command === 'indent') {
        this.indent();
      } else if (command === 'outdent') {
        if (this.currentList()) {
          document.execCommand('outdent');
        }
      } else {
        document.execCommand(command, false, null);
      }
      this.onInput();
    },
    toggleList(command) {
      const kind = { bullet: 'bullet', number: '1', letter: 'a' }[command];
      const list = this.currentList();
      if (list && listKind(list) === kind) {
        // The same button again ends the list
        document.execCommand(kind === 'bullet' ? 'insertUnorderedList' : 'insertOrderedList');
        return;
      }
      if (kind === 'bullet') {
        document.execCommand('insertUnorderedList');
        return;
      }
      if (!list || list.nodeName !== 'OL') {
        document.execCommand('insertOrderedList');
      }
      this.setKind(this.currentList(), kind);
    },
    indent() {
      const list = this.currentList();
      if (!list || this.listDepth(list) >= MAX_LIST_DEPTH) {
        return;
      }
      document.execCommand('indent');
      const nested = this.currentList();
      const parent = nested && nested.parentElement.closest('ol, ul');
      // A numbered list one level down is lettered, then roman, as in Word
      if (nested && nested !== list && parent && nested.nodeName === 'OL' && parent.nodeName === 'OL') {
        this.setKind(nested, NESTED_LIST_KIND[listKind(parent)]);
      }
    },
    setKind(list, kind) {
      if (!list || list.nodeName !== 'OL') {
        return;
      }
      if (kind === '1') {
        list.removeAttribute('type');
      } else {
        list.setAttribute('type', kind);
      }
      list.style.listStyleType = '';
    },
    selectionNode() {
      const selection = window.getSelection();
      if (!selection.rangeCount || !this.$refs.input.contains(selection.anchorNode)) {
        return null;
      }
      const node = selection.anchorNode;
      return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    },
    currentList() {
      const node = this.selectionNode();
      const list = node && node.closest('ol, ul');
      return list && this.$refs.input.contains(list) ? list : null;
    },
    currentBlock() {
      const node = this.selectionNode();
      const block = node && node.closest('h2, h3');
      return block && this.$refs.input.contains(block) ? block.nodeName.toLowerCase() : null;
    },
    listDepth(list) {
      let depth = 0;
      for (let node = list; node && node !== this.$refs.input; node = node.parentElement) {
        if (node.nodeName === 'OL' || node.nodeName === 'UL') {
          depth++;
        }
      }
      return depth;
    },
    onInput() {
      this.emitted = richTextFromElement(this.$refs.input, this.options);
      this.$emit('input', this.emitted);
      this.updateActive();
    },
    onKeydown(event) {
      // Tab moves a list item a level down or up; outside a list it moves on to the next field as usual
      if (event.key === 'Tab' && this.currentList()) {
        event.preventDefault();
        this.run(event.shiftKey ? 'outdent' : 'indent');
      }
    },
    onPaste(event) {
      // Pasted text keeps what these fields allow (headers, lists, bold, italic, underline), nothing else
      event.preventDefault();
      const clipboard = event.clipboardData || window.clipboardData;
      const html = clipboard.getData('text/html');
      if (html) {
        const body = new DOMParser().parseFromString(html, 'text/html').body;
        const headings = this.headings ? rankHeadings(body).headings : false;
        // Pasted as HTML, so it also keeps its lists when the field otherwise stores lines
        document.execCommand('insertHTML', false, richTextFromElement(body, { headings }));
      } else {
        document.execCommand('insertText', false, clipboard.getData('text/plain'));
      }
      this.onInput();
    },
    updateActive() {
      if (!this.selectionNode()) {
        return;
      }
      const list = this.currentList();
      const kind = list ? listKind(list) : null;
      const block = this.currentBlock();
      Object.assign(this.active, {
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        underline: document.queryCommandState('underline'),
        h2: block === 'h2',
        h3: block === 'h3',
        bullet: kind === 'bullet',
        number: kind === '1' || kind === 'i',
        letter: kind === 'a'
      });
    }
  }
};
</script>
